import { plural } from 'lib/plural';
import { BillingDataPeriod } from 'reactApp/api/billing/data/BillingDataAPICall.types';
import { type BillingDataSubscriptionInfo, BillingDataSubscriptionInfoStatus } from 'reactApp/modules/billingData/billingData.types';
import type { Product } from 'reactApp/types/Billing';
import { addPeriodToDate, isYear2Period, toDaysFromMS } from 'reactApp/utils/Period';

export function getCurrentSubscriptionPeriod(subs: BillingDataSubscriptionInfo) {
    switch (subs.currentPeriod) {
        case BillingDataPeriod.DISCOUNT:
            return subs.productDiscountPeriod!;
        case BillingDataPeriod.TRIAL:
            return subs.productTrialPeriod!;
        case BillingDataPeriod.REGULAR:
        default:
            return subs.productPeriod!;
    }
}

/**
 * @warning Метод не работает для модифицированных подписок, а именно,
 * где цена продления нерегулярного периода, отличается от цены продукта (но таких не бывает).
 * Модифицированная цена регулярного продукта проблем не вызывает.
 *
 * @description возвращает СЛЕДУЮЩИЙ период подписки при попытке РУЧНОГО ПРОДЛЕНИЯ.
 *
 * @param subscription - подписка
 */
export const getNextSubsctiptionPeriod = ({
    currentPeriod,
    productDiscountPeriod,
    productPeriod,
    renewalCost,
    discountCost,
    regularCost,
}: BillingDataSubscriptionInfo) => {
    /**
     * Вот это сделанно для скидки в оттоке, так как она возможна ТОЛЬКО в регулярном периоде,
     * но при этом renewalCost у нее будет отличаться от регулярной цены продукта (на 30 или 50 процентов).
     * И поэтому, сопоставление цены продления с ценой продукта здесь не имеет смысла, но мы точно знаем,
     * что такое возможно только в регулярном периоде.
     *
     * В конце-концов, находясь в регулярном периоде, мы можем попасть только в регулярный при продлении.
     */
    if (currentPeriod === BillingDataPeriod.REGULAR) {
        return productPeriod;
    }
    if (renewalCost === discountCost) {
        return productDiscountPeriod;
    }
    if (renewalCost === regularCost) {
        return productPeriod;
    }
    return null; // необработанные новые типы подписок, а так же триалы, фримиумы, т.е. то куда мы не можем попасть при продлении
};

export function getCurrentProductPeriod(product: Product, currentPeriod?: BillingDataPeriod) {
    switch (currentPeriod) {
        case BillingDataPeriod.DISCOUNT:
            return product.discountPeriod!;
        case BillingDataPeriod.TRIAL:
            return product.trialPeriod!;
        case BillingDataPeriod.REGULAR:
        default:
            return product.period;
    }
}

export function getCurrentProductPrice(product: Product, currentPeriod?: BillingDataPeriod) {
    switch (currentPeriod) {
        case BillingDataPeriod.DISCOUNT:
            return product.discountPrice!;
        case BillingDataPeriod.TRIAL:
            return 1;
        case BillingDataPeriod.REGULAR:
        default:
            return product.price;
    }
}

/**
 * @description определяется возможность продления подписки на основании данных.
 *
 * @param subscription - подписка
 * @param suspendedIsRenewable - учитывать статус SUSPENDED как доступный для продления
 */
export function isSubscriptionRenewable(subscription: BillingDataSubscriptionInfo, suspendedIsRenewable = false) {
    const { expires, renewable, status, productPeriod } = subscription;
    const isActive = status === BillingDataSubscriptionInfoStatus.ACTIVE;
    const isSuspended = status === BillingDataSubscriptionInfoStatus.SUSPENDED;
    const isActiveByStatus = suspendedIsRenewable ? isActive || isSuspended : isActive;
    const isActiveByExpires = Date.now() < expires;
    const isYear2Product = productPeriod && isYear2Period(productPeriod);
    const isRenewable = isActiveByStatus && renewable && !isYear2Product;
    return isActiveByExpires && isRenewable;
}

export const subtractSubscriptionDates = (subscriptionStart: number, subscriptionPeriod: string, dateNow: number = Date.now()): number => {
    const daysLeft = toDaysFromMS(addPeriodToDate(subscriptionStart, subscriptionPeriod) - dateNow);

    if (!daysLeft || subscriptionStart > dateNow) {
        return 0;
    }
    return daysLeft;
};

export const createDaysStringPlural = (numberOfDays: number): string | undefined => {
    if (numberOfDays <= 0) {
        return;
    }

    return `${numberOfDays} ${['день', 'дня', 'дней'][plural(numberOfDays)]}`;
};

export const getUpgradeSubscriptionId = (subscription: BillingDataSubscriptionInfo, tariffsMap: Record<number, string>) => {
    const subSpace = subscription?.space?.space;
    const subPeriod = subscription?.productPeriod;
    if (!tariffsMap || !subSpace || !tariffsMap[subSpace] || !subPeriod) {
        return null;
    }

    return tariffsMap[subSpace].replace('date', subPeriod);
};

export const formatUpgradeSubscriptionPrice = (period?: string, price?: number) => {
    if (!period || !price) {
        return 0;
    }

    if (period === '1y' && price) {
        return Math.ceil(price / 12);
    }

    return price;
};
